import OlSnap from 'ol/interaction/Snap';
import { distance as coordinateDistance } from 'ol/coordinate';
import { boundingExtent } from 'ol/extent';

import { Oval } from 'cadenza/map/geom/oval';

export class SnapInteraction extends OlSnap {

  #pixelTolerance;
  #edge;
  #segmentCircleGeometry;
  #currentMapBrowseEvent;

  constructor (options) {
    super(options);
    // Use the OL defaults:
    this.#pixelTolerance = options.pixelTolerance ?? 10;
    this.#edge = options.edge ?? true;
    // The GEOMETRY_SEGMENTERS.Circle is overridden below.
    // Before, we store the original function for use in the overriding one.
    this.#segmentCircleGeometry = this.GEOMETRY_SEGMENTERS.Circle;
    this.GEOMETRY_SEGMENTERS.Rectangle = this._segmentRectangleGeometry.bind(this);
    this.GEOMETRY_SEGMENTERS.Circle = this._segmentCircleLikeGeometry.bind(this);
  }

  handleEvent (mapBrowserEvent) {
    this.#currentMapBrowseEvent = mapBrowserEvent;
    return super.handleEvent(mapBrowserEvent);
  }

  snapTo (pixel, pixelCoordinate, map) {
    if (this.#currentMapBrowseEvent.originalEvent.shiftKey || this.#currentMapBrowseEvent.snapped) {
      return null;
    }

    const snappedPoint = this._calculateSnapPoint(pixel, pixelCoordinate, map);
    if (snappedPoint) {
      this.#currentMapBrowseEvent.snapped = true;
    }
    return snappedPoint;
  }

  _calculateSnapPoint (pixel, pixelCoordinate, map) {
    const lowerLeft = map.getCoordinateFromPixel([ pixel[0] - this.#pixelTolerance, pixel[1] + this.#pixelTolerance ]);
    const upperRight = map.getCoordinateFromPixel([ pixel[0] + this.#pixelTolerance, pixel[1] - this.#pixelTolerance ]);
    const box = boundingExtent([ lowerLeft, upperRight ]);

    const segments = this.rBush.getInExtent(box);
    const isOval = segments.length > 0 && segments[0].feature.getGeometry() instanceof Oval;

    if (isOval) {
      const feature = segments[0].feature;
      const ovalGeometry = feature.getGeometry();
      const vertexHit = segments.find(segmentData => segmentData.feature === feature && segmentData.segment.isVertex);

      let vertex;
      if (vertexHit) {
        vertex = vertexHit.segment[0];
      } else if (this.#edge) {
        vertex = ovalGeometry.getClosestPoint(pixelCoordinate);
      }
      if (vertex) {
        let vertexPixel = map.getPixelFromCoordinate(vertex);
        if (coordinateDistance(pixel, vertexPixel) <= this.#pixelTolerance) {
          vertexPixel = [ Math.round(vertexPixel[0]), Math.round(vertexPixel[1]) ];
          return ({
            snapped: true,
            vertex,
            vertexPixel
          });
        }
      }
    }

    return super.snapTo(pixel, pixelCoordinate, map);
  }

  _segmentRectangleGeometry (segments, geometry) {
    const centerVertex = geometry.getCenter();
    segments.push([ centerVertex, centerVertex ]);

    const vertices = geometry.getVertices();
    for (let i = 0, ii = vertices.length - 1; i < ii; ++i) {
      segments.push(vertices.slice(i, i + 2));
    }
  }

  _segmentCircleLikeGeometry (segments, geometry) {
    if (geometry instanceof Oval) {
      this._segmentOvalGeometry(segments, geometry);
      return;
    }
    this.#segmentCircleGeometry(segments, geometry);
  }

  _segmentOvalGeometry (segments, geometry) {
    const vertices = [ geometry.getCenter(), ...geometry.getCorners() ]; // probably need to be rotated? Or getCorners needs to already return rotated corners...
    vertices.forEach(cornerCoordinate => {
      const cornerSegment = [ cornerCoordinate, cornerCoordinate ];
      cornerSegment.isVertex = true;
      segments.push(cornerSegment);
    });

    const coordinates = geometry.generateCoordinates(); // already returns rotated coordinates
    for (let i = 0, ii = coordinates.length - 1; i < ii; ++i) {
      segments.push(coordinates.slice(i, i + 2));
    }
  }

}
